import { AfterContentInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { LicenseService } from 'src/app/core/services/license.service';
import { EntityService } from 'src/app/core/services/entity.service';
import { DeviceService } from 'src/app/core/services/device.service';
import { Package } from 'src/app/core/models/package.model';
import { PackageType } from 'src/app/core/models/package-type.model';
import { Entity } from 'src/app/core/models/entity.model';
import { Device } from 'src/app/core/models/device.model';
import { User } from 'src/app/core/models/user.model';
import { SettingsService } from 'src/app/core/services/settings.service';
import { TranslateService } from '@ngx-translate/core';
import { CustomValidators } from 'src/app/shared/helpers/custom-validators';
import * as utils from 'src/app/shared/helpers/utils';
import { catchError, Subscription, take, throwError } from 'rxjs';
import { Groups } from 'src/app/core/models/group.model';
import { License, LicenseStatus, StatusDate } from 'src/app/core/models/license.model';
import { Policy } from 'src/app/core/models/policy.model';
import { ConfigService } from 'src/app/core/services/config.service';

@Component({
  selector: 'hbo-package-actions',
  templateUrl: './package-actions.component.html',
  styleUrls: ['./package-actions.component.scss']
})
export class PackageActionsComponent implements OnInit, AfterContentInit {

  @Input() package!: Package;
  @Output() reload: EventEmitter<any> = new EventEmitter();

  action = 'create';
  reactiveToRole = 1;
  entity!: Entity;
  entityToSettings!: Entity;
  policy!: Policy;

  user: User = utils?.localStorageGetItem('user');
  confirmationModal: any;

  categories = [{ id: 1, name: 'OPERATIVE' }, { id: 2, name: 'DEMO' }];
  periods = [{ id: 1, name: 'monthly', disabled: false }, { id: 2, name: 'annual', disabled: false }];
  devices: Device[] = [];
  packageTypeId: FormControl = new FormControl(null, Validators.required);
  packagePeriodId: FormControl = new FormControl(null, Validators.required);
  packageCategoryd: FormControl = new FormControl(1, Validators.required);
  entityId: FormControl = new FormControl(null, Validators.required);

  packageForm = this.formBuilder.nonNullable.group({
    id: [''],
    amount: ['', Validators.required],
    entityId: this.entityId,
    packageTypeId: this.packageTypeId,
    packageCategoryId: this.packageCategoryd,
    packagePeriodId: this.packagePeriodId,
    testDays: [''],
    apps: [''],
    private: [false],
    ownerEntityId: [1],
    assignedEntityId: [''],
    packageStatusId: [1],
    deviceId: ['']
  });

  entities: Entity[] = [];
  packageTypes: PackageType[] = [];
  allPackageTypes: PackageType[] = [];
  selectedPackageType!: PackageType;
  selectedCategory!: string;
  selectedPeriod!: string;


  packageCategoryIdLstnr: Subscription = new Subscription;
  packageTypeIdLstnr: Subscription = new Subscription;
  packageEntityLstnr: Subscription = new Subscription;

  createdPackages!: Package[];
  createdPackagesIncludesAssigned!: Package[];

  existingPackageId!: number | null;

  group!: string;
  groups: Groups = new Groups;

  editComments: boolean = false;
  totalPackages: number = 0;
  createdPackagesNumber: number = 0;
  modifyingPackage: boolean = false;
  endModify: number | null = 0;
  createdPackageId: number | null = 0;
  errorCreation: boolean = false;

  //dynamodb cannot contain an index label with empty string
  get displayComments() { return this.package.comments; }
  set displayComments(v) { v == 'empty' ? this.package.comments = '' : this.package.comments = v; }

  get canReassign(): boolean {
    if (!this.package || this.action !== 'edit') return false;
    if (this.user.roleId === 4 || this.user.profileId === 2) return false;
    const isNoDevice = this.groups?.noDevice?.some(
      (ptId: number) => ptId === this.package.packageTypeId
    );
    const isAtBase = !!this.package.assigned?.isBase;
    return isNoDevice && isAtBase;
  }


  get linksInvisionDevice(): boolean {
    return this.groups.fit.includes(this.package?.packageTypeId);
  }

  // Only filled when the assign button is clicked. Binding the package straight into the modal's
  // presetPackage would prefill its type control, and the patchValue({}) in the modal's ngOnInit then
  // emits with a valid form: that fires the paged availability search on every details load.
  packageToAssign: Package | null = null;

  // Mirrors the rule the package list applies inline: a package still parked at a head office or a
  // distributor can be handed down to an optical shop, so the details view has to offer it as well.
  // Kept apart from canReassign because that one covers the no-device-at-base flow and its own modal.
  get canAssignFromParent(): boolean {
    if (!this.package || this.action !== 'edit') return false;
    if (this.user.roleId === 4 || this.user.profileId === 2) return false;
    return this.package.packageStatusId === 1
      && this.package.assigned?.entityTypeId !== 4;
  }

  // A package parked at a base entity belongs, for display purposes, to whoever owns that base
  // entity: the base is only a holding spot, so its own name says nothing useful.
  effectiveOwner(_package: Package): Entity {
    if (!_package?.assigned?.isBase) return _package?.assigned;
    const childEntities: Entity[] = this.user.childEntities || [];
    const base = childEntities.find((entity: Entity) => entity.id === _package.assignedEntityId);
    if (!base) return _package.assigned;
    const userEntity = this.configSvc.currentConfigValue.userEntity;
    return childEntities.find((entity: Entity) => entity.id === base.parentEntityId)
      ?? (userEntity?.id === base.parentEntityId ? userEntity : _package.assigned);
  }


  entitiesForPackageCreation: Entity[] = [];

  // "Continue creating" keeps the form open, so the type it was created with would carry over. Clearing
  // it starts the next package from a clean slate and widens the destinations back to every entity.
  continueCreating = () => {
    this.packageForm.get('packageTypeId')?.reset();
  }

  private refreshEntitiesForPackageCreation = () => {
    const currentPackageTypeId = this.packageForm.get('packageTypeId')?.value;
    const isNoDevice = this.groups?.noDevice?.some(
      (ptId: number) => ptId === currentPackageTypeId
    );
    this.entitiesForPackageCreation = isNoDevice
      ? this.entities.filter((e: Entity) => e.entityTypeId === 4)
      : this.entities;
  }

  //error while creating packages, for example, login expired
  errorWhileCreating!: string | null;

  // Reason the backend refused the creation. Without it the progress modal only says "error
  // creating the package" and the actual cause is lost in the console.
  creationError: string | null = null;

  private static readonly CREATION_ERROR_KEYS: Record<string, string> = {
    ENTITY_HAS_ACTIVE_DEPICT_LICENSE: 'COMMON.REASSIGN.ERRORS.ENTITY_HAS_ACTIVE_DEPICT_LICENSE',
  };

  // Translated key when the backend sends a known code, its own text otherwise. Both survive the
  // translate pipe: an unknown key is rendered verbatim.
  private resolveCreationError(err: unknown): string {
    const body = (err as { error?: { code?: string; error?: string; message?: string } })?.error;
    const key = body?.code ? PackageActionsComponent.CREATION_ERROR_KEYS[body.code] : undefined;

    return key || body?.error || body?.message || 'LICENSE.ERRORCREATINGPACKAGE';
  }

  licenseInactiveData!: string;

  // Which same-group warning wording the reassign modal asked for.

  licenseWarningNoDevice = false;
  underdstoodButton!: boolean;
  haveLicencesSameGroup!: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    public router: Router,
    private licenseService: LicenseService,
    private entityService: EntityService,
    private deviceService: DeviceService,
    private location: Location,
    private settingsService: SettingsService,
    private translateService: TranslateService,
    public configSvc: ConfigService
  ) {
    this.user.childEntities = this.configSvc.currentConfigValue.childEntities;
    // override the route reuse strategy
    this.router.routeReuseStrategy.shouldReuseRoute = function () {
      return false;
    }

    this.router.events.subscribe((evt) => {
      if (evt instanceof NavigationEnd) {
        // trick the Router into believing it's last link wasn't previously loaded
        this.router.navigated = false;
        // if you need to scroll back to top, here is the right place
        window.scrollTo(0, 0);
      }
    });
  }

  ngOnInit() {

    this.packageEntityLstnr = this.packageForm.controls.entityId.valueChanges.subscribe(_ => {
      this.existingPackageId = null;
      const entity = this.user.childEntities!.find((entity: Entity) => entity.id == Number(this.packageForm.controls.entityId.value));
      if (entity?.entityTypeId != 4) {
        this.packageForm.controls.packageCategoryId.patchValue(1);
      }
    })
    this.packageCategoryIdLstnr = this.packageForm.controls.packageCategoryId.valueChanges.subscribe(value => {
      const currentPackageTypeId = this.packageForm.controls.packageTypeId.value;
      const isNoDevice = this.groups?.noDevice?.some((ptId: number) => ptId === currentPackageTypeId);
      if (value == '2') {
        this.packageForm.controls.packagePeriodId.patchValue(2);
      } else if (!isNoDevice) {
        this.packageForm.controls.packagePeriodId.patchValue(null);
      }
    })
    this.packageTypeIdLstnr = this.packageForm.controls.packageTypeId.valueChanges.subscribe(type => {
      // Only a real type change narrows the destinations, so this is the one place that has to recompute
      this.refreshEntitiesForPackageCreation();
      if (this.groups.mimesys.includes(type) || this.groups.noDevice.includes(type) || this.groups.fit.includes(type)) {
        this.periods = this.periods.map(period => period.id === 1 ? { ...period, disabled: true } : { ...period, disabled: false });
        this.packageForm.controls.packagePeriodId.patchValue(2);
      } else {
        this.periods = this.periods.map(period => period.id === 1 ? { ...period, disabled: false } : { ...period, disabled: false });
        this.packageForm.controls.packageCategoryId.value == '2'
          ? this.packageForm.controls.packagePeriodId.patchValue(2)//if demo category set peridicity as annual to validate the form
          : this.packageForm.controls.packagePeriodId.patchValue(null)
      }
    })




    const packageId = this.route.snapshot.paramMap.get('id');
    if (!packageId) {
      this.package = { id: 0 } as Package;
    } else {
      this.action = 'edit';
      this.packageForm.get('id')?.disable();
      this.packageForm.get('amount')?.disable();
      this.packageForm.get('entityId')?.disable();
      this.packageForm.get('packageTypeId')?.disable();
      this.packageForm.get('packageCategoryId')?.disable();
      this.packageForm.get('packagePeriodId')?.disable();
      this.packageForm.get('testDays')?.disable();
      this.packageForm.get('apps')?.disable();
      this.packageForm.get('private')?.disable();
      this.licenseService.showPackage(packageId).subscribe(_package => { this.package = _package; this.ngAfterContentInit(); });
    }
    //get package Id details
    //this.entityService.getOnlyParents().subscribe(entities => this.entities = entities.data);
    this.entities = this.configSvc.currentConfigValue.childEntities!;
    this.refreshEntitiesForPackageCreation();

    !this.configSvc.currentConfigValue.packageTypes
      ? this.licenseService.getPackageTypes().subscribe(packageTypes => {
        this.allPackageTypes = packageTypes.data;
        this.configSvc.setConfigParamsValue('packageTypes', this.allPackageTypes);
      })
      : this.allPackageTypes = this.configSvc.currentConfigValue.packageTypes;

    this.packageForm.controls.entityId.setValidators([Validators.required, CustomValidators.ShopNeeded(this.packageForm, this.user.childEntities!, this.configSvc.currentConfigValue, 'entityId')])
    this.packageForm.controls.packageCategoryId.setValidators([Validators.required, CustomValidators.ShopNeeded(this.packageForm, this.user.childEntities!, this.configSvc.currentConfigValue, 'packageCategoryId')])
    this.packageForm.controls.packageTypeId.setValidators([Validators.required, CustomValidators.ShopNeeded(this.packageForm, this.user.childEntities!, this.configSvc.currentConfigValue, 'packageTypeId')])


    if (this.action === 'create') {
      this.reactiveToRole = (this.action == 'create' ? this.user.role!.id : 2);
    }
  }

  ngOnDestroy() {
    this.packageCategoryIdLstnr.unsubscribe();
    this.packageTypeIdLstnr.unsubscribe();
  }

  ngAfterContentInit() {
    if (this.package && this.package.id === 0) {
      this.filterPackageTypesByPolicy(this.user.entityId);
    }

    if (this.package && this.package.id !== 0) {
      this.selectedPackageType = this.packageTypes.find(packageType => packageType.id === this.package.packageTypeId)!;
      if (this.user.entityId !== this.package.assignedEntityId) {
        this.entity = this.user.childEntities!.find((entity: Entity) => entity.id === this.package.assignedEntityId!)!;
      } else {
        this.entity = this.user.entity;
      }
      switch (this.groups.mimesys.includes(this.package.packageTypeId)) {
        case true:
          if (this.package.deviceId) this.deviceService.get(this.package.deviceId!).subscribe({
            next: devices => {
              this.filterDevicesByPackageType([devices]);
            },
            error: () => this.devices = []
          })
          else this.devices = []
          break;
        default:
          this.deviceService.getEntityDevices(this.entity.id).subscribe({
            next: devices => {
              this.filterDevicesByPackageType(devices);
            },
            error: () => this.devices = []
          });
          break;
      }


      this.filterPackageTypesByPolicy(this.entity.id);

      this.packageForm.patchValue({
        id: this.package.id?.toString(),
        entityId: this.package.ownerEntityId?.toString(),
        testDays: this.action !== 'create' ? this.package.testDays?.toString() : this.package.defaultTestDays ? this.package.defaultTestDays.toString() : this.package.testDays ? this.package.testDays?.toString() : this.package.packageType!.testDays?.toString(),
        private: this.package.packageType!.private,
        packagePeriodId: this.package.packagePeriodId.toString(),
        packageCategoryId: this.package.packageCategoryId.toString(),
        packageTypeId: this.package.packageTypeId.toString()
      });
    }
  }

  checkExisitingPackagesBeforeSaving() {
    this.haveLicencesSameGroup = false;//reset inactive license bool
    this.underdstoodButton = false;//reset checking 
    this.packageForm.markAllAsTouched();
    this.endModify = 0;
    this.createdPackageId = 0;
    this.errorCreation = false;
    if (this.packageForm.valid) {
      utils.toogleModal('#confirmationCreatePackageModal', false)
      this.errorWhileCreating = null;
      //it will be needed fro admin to create a package and assign to an optical as a modification

      //filter packages back only multiple for entities so we recover every package and filter after
      //------------------------------------------------------------------------------------------------
      //let findAllPackagesFromEntity = { status: 3, type: this.packageForm.controls.packageTypeId.value, category: this.packageForm.controls.packageCategoryId.value, entities: `[${this.packageForm.controls.entityId.value}]`, perPage: -1 };
      //------------------------------------------------------------------------------------------------
      this.createdPackages = [];
      this.createdPackagesIncludesAssigned = [];
      let packagesToObtain: number[] = [];
      if (this.groups.mimesys.includes(this.packageForm.controls.packageTypeId.value)) {
        packagesToObtain = this.groups.mimesys.filter(app => app != this.packageForm.controls.packageTypeId.value);
      } else if (this.groups.fit.includes(this.packageForm.controls.packageTypeId.value)) {
        packagesToObtain = this.groups.fit.filter(app => app != this.packageForm.controls.packageTypeId.value)
      }
      if (this.user.childEntities!.find((entity: Entity) => entity.id == Number(this.packageForm.controls.entityId.value))?.entityTypeId == 4) {
        let findAllPackagesFromEntity = { category: this.packageForm.controls.packageCategoryId.value, entities: `[${this.packageForm.controls.entityId.value}]`, perPage: 10, order: 'desc' };//limited to 10 packages by operative
        this.licenseService.availablePackages({ ...findAllPackagesFromEntity }).subscribe(
          (res) => {
            this.createdPackagesIncludesAssigned = res.data;
            this.createdPackages = res.data;
            //only from the group
            this.createdPackages = this.createdPackages.filter(pack => packagesToObtain.includes(pack.packageTypeId));
            //only by same category
            this.createdPackages = this.createdPackages.filter(pack => pack.packageCategoryId == Number(this.packageForm.controls.packageCategoryId.value));
            //only by same periodicity
            this.createdPackages = this.createdPackages.filter(pack => pack.packagePeriodId == Number(this.packageForm.controls.packagePeriodId.value));
            //only assigned,test,active status
            this.createdPackages = this.createdPackages.filter(pack => ([2, 3]).includes(Number(pack.status.id)));

            //not include active packages from modification still cancelable
            //this.createdPackages = this.createdPackages.filter(pack => !pack.packageOriginId || (pack.packageOriginId && !this.leaveOutPackageFromUpgradeOnlyInheritedLicenses(pack)));
          },
          (error) => {
            this.errorWhileCreating = 'MODAL.DISMISS';
            console.error('Error occurred:', error);
          });
      }
    }
  }

  selectedPackage(_id: number) {
    if (_id) {
      this.existingPackageId = _id
    }
    else {
      this.existingPackageId = null;
    }
  }

  save() {
    this.detectSomeLicenseGroupInactive();
    if (this.haveLicencesSameGroup && !this.existingPackageId && !this.underdstoodButton && this.user.childEntities!.find((entity: Entity) => entity.id == Number(this.packageForm.controls.entityId.value))?.entityTypeId == 4) {
      /*detect if it exists another package from same group
           then show to admin a warning modal asking for checking that optical shop has more than one device*/
      //utils.forceModal('#confirmationCreatePackageModal', true);
      setTimeout(() => {//implement delay to prevent from previous modal dismiss
        utils.forceModal('#confirmationModal-admin-some-license', false);
      }, 500); // .5 segundos
    } else {
      console.log(this.packageForm.value);
      this.packageForm.markAllAsTouched();
      this.endModify = 0;
      this.createdPackageId = 0;
      this.errorCreation = false;
      this.packageForm.markAllAsTouched();
      if (this.packageForm.valid) {
        this.packageForm.controls.packageCategoryId.value == '2' ? this.packageForm.controls.testDays.patchValue('0') : null;
        if (this.action === 'create') {
          //set owner
          this.totalPackages = Number(this.packageForm.get('amount')?.value!);
          this.createdPackagesNumber = 0;
          if (this.packageForm.controls.entityId.value != '1') {
            const entityMatch: Entity = this.user.childEntities!.find((child: Entity) => child.id == Number(this.packageForm.controls.entityId.value))!;
            const parentEntity: Entity = this.user.childEntities!.find((child: Entity) => child.id == Number(entityMatch.parentEntityId))!;
            this.packageForm.controls.ownerEntityId.patchValue(parentEntity ? parentEntity.id : 1);
            this.packageForm.controls.assignedEntityId.patchValue(this.packageForm.controls.entityId.value);
          } else {
            this.packageForm.controls.assignedEntityId.patchValue(this.packageForm.controls.entityId.value);
          }
          if (this.existingPackageId) {
            this.packageForm.controls.amount.patchValue('1');
            if (this.packageForm.controls.packageCategoryId.value != '2') {
              this.packageForm.controls.assignedEntityId.patchValue(String(this.packageForm.controls.ownerEntityId.value));
            }
          }
          this.iterateCreation(this.packageForm, Number(this.packageForm.controls.amount.value)).finally(() => { });
          /*this.licenseService.insertPackage({ ...this.packageForm.value } as unknown as Package).pipe(take(1)).subscribe(res => {
            if (Number(this.packageForm.controls.entityId.value) != 1) {
              if (this.existingPackageId) {
                //modify selected package with the new created package
                let finalPackage: Package = res[0];
                const patchedPackage: Package = new Package();
                patchedPackage.id = this.existingPackageId;
                patchedPackage.packageTypeId = finalPackage.packageTypeId;
                console.log(patchedPackage);
                patchedPackage.upgrade = finalPackage.id;
                this.licenseService.updatePackage(patchedPackage).subscribe(res => {
                  this.router.navigate(['/licenses/packages/edit/' + finalPackage.id]);
                });
              } else {
                if ([1, 2, 3, 4].includes(Number(this.packageForm.controls.packageTypeId.value))) {
                  this.setMimesysSettings('navigate', Number(this.packageForm.controls.amount.value), res[0].id);
                } else {
                  if ([5, 6, 8].includes(Number(this.packageForm.controls.packageTypeId.value))) {
                    this.setExternalSettings('navigate', Number(this.packageForm.controls.amount.value), 5, res[0].id);
                  } else if ([7].includes(Number(this.packageForm.controls.packageTypeId.value))) {
                    this.setExternalSettings('navigate', Number(this.packageForm.controls.amount.value), 7, res[0].id);
                  }
                }
              }
  
            } else {
              this.router.navigate(['/licenses/packages/edit/' + + (res[0].id ? res[0].id : '')]);
            }
          });*/
        } else if (this.action === 'edit') {
          /*this.licenseService.updatePackage({ ...this.packageForm.value } as unknown as Package).subscribe(res => {
            if ([1, 2, 3, 4].includes(Number(this.packageForm.controls.packageTypeId.value))) {
              this.setMimesysSettings('reload');
            } else {
              this.reload.emit('reload');
            }
          });*/
        }
      }
    }
  }

  setSettingsAncestor(): Promise<{ data: any; base64: string }> {
    this.entityToSettings = this.user.childEntities!.find((entity: Entity) => entity.id === Number(this.packageForm.controls.entityId.value))!;
    return new Promise((resolve, reject) => {
      this.entityService.getAncestor(this.entityToSettings).subscribe(
        async res => {
          let parentEntity = res.parentEntity;
          let object = {
            "hierarchy": {
              "shop_id": String(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id),
              "ancestor": {
                "shop_id": String(parentEntity?.internalCode ? parentEntity?.internalCode : parentEntity?.id),
                "ancestor": this.entityToSettings?.entityTypeId != 2
                  ? {
                    "shop_id": String(parentEntity?.parentEntity?.internalCode ? parentEntity?.parentEntity?.internalCode : parentEntity?.parentEntity?.id),
                    "ancestor": parentEntity?.parentEntity?.entityTypeId == 2
                      ? {
                        "shop_id": String(parentEntity?.parentEntity?.parentEntity?.internalCode ? parentEntity?.parentEntity?.parentEntity?.internalCode : parentEntity?.parentEntity?.parentEntity?.id),
                        "ancestor": null
                      }
                      : null
                  }
                  : null
              }
            }
          }
          resolve({ data: object, base64: utils.objectToBase64(object) });
          //resolve({ data: object, base64: utils.objectToBase64(object) });
        });
    });
  }

  async setExternalSettings(_mode: string, _amount: number, _app: number, _id?: number) {
    // Set invision and thickness settings. If there is no invision Settings, there can't be thickness settings.
    // If there are invision settings we still have to check for the thickness ones, because we can have invision with no thickness
    let SettingsAncestor = await this.setSettingsAncestor();

    if ([5, 7].includes(_app)) {
      this.settingsService.getInvision(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id, SettingsAncestor.base64, this.entityToSettings.entityTypeId).subscribe(
        res => {
          if (_app == 7) {
            this.settingsService.getThickness(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id, SettingsAncestor.base64, this.entityToSettings.entityTypeId).subscribe(
              res => { },
              err => {
                if (err.status == 404) {
                  this.settingsService.updateThickness(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id, SettingsAncestor.base64, this.entityToSettings.entityTypeId, { "settings": null }).subscribe(
                    res => {
                      console.log('settings depict ok', res)
                    },
                    err => {
                      console.log('error', err);
                      //this.router.navigate(['/licenses/packages/edit/' + (_id ? _id : '')]);
                    });
                }
              })
          }
        },
        err => {
          if (err.status == 404) {
            this.settingsService.updateInvision(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id, SettingsAncestor.base64, this.entityToSettings.entityTypeId, { "settings": { "timezone": this.entityToSettings.timezone } }).subscribe(
              res => {
                if (_app == 7) {
                  this.settingsService.updateThickness(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id, SettingsAncestor.base64, this.entityToSettings.entityTypeId, { "settings": null }).subscribe(
                    res => {
                      console.log('settings depict ok', res)
                    },
                    err => {
                      console.log('error', err)
                      //this.router.navigate(['/licenses/packages/edit/' + (_id ? _id : '')]);
                    });
                }
                switch (_mode) {
                  case 'reload':
                    this.reload.emit('reload');
                    break;
                  case 'navigate':
                    /*if (_amount > 1) this.router.navigate(['/licenses/packages'])
                    else this.router.navigate(['/licenses/packages/edit/' + (_id ? _id : '')]);*/
                    break;
                  default: break;
                }
              },
              err => {
                //this.router.navigate(['/licenses/packages/edit/' + (_id ? _id : '')]);
              });
          }
        }
      )
    }

    if (this.groups.mimesys.includes(_app)) {

      this.settingsService.getMimesys(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id, SettingsAncestor.base64, this.entityToSettings.entityTypeId, "", "en-GB").subscribe(
        res => {
          console.log("Settings Mimesys exists");
        },
        err => {
          if (err.status == 404) {
            this.settingsService.updateMimesys(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id, SettingsAncestor.base64, this.entityToSettings.entityTypeId, { "settings": { "timezone": this.entityToSettings.timezone } }).subscribe(
              res => {
                switch (_mode) {
                  case 'reload':
                    this.reload.emit('reload');
                    break;
                  case 'navigate':
                    break;
                  default: break;
                }
              },
              err => {
                console.log("Error creating settings Mimesys");
              });
          }
        }
      )
    }
  }

  setPackageLabelDate(_label: string) {
    if (this.package.licenses) {
      return this.package.licenses.reduce<Date | null>((min, item) => {
        if (item.statusDate) {
          if (item.statusDate[_label]) {
            const date = new Date(item.statusDate[_label]);
            if (min)
              min = date < min ? date : min;
            else
              min = date;
          }
          return min;
        } else {
          return null
        }

      }, null);
    } else {
      return
    }

  }


  setMimesysSettings(_mode: string, _amount: number, _id?: number) {
    switch (_mode) {
      case 'reload':
        if (this.groups.mimesys.includes(Number(this.packageForm.controls.packageTypeId.value))) {
          //settings mimesys
          //generate appuser and appPwd
          let entity: Entity = this.user.childEntities!.find(entity => entity.id == Number(this.packageForm.controls.assignedEntityId.value))!;
          if (entity && entity.entityTypeId === 4) {
            this.settingsService.getMimesys(Number(this.packageForm.controls.assignedEntityId.value), null, entity.parentEntityId, entity.email, this.translateService.currentLang).subscribe(res => {
              this.reload.emit('reload');
            });
          } else {
            this.reload.emit('reload');
          }
        }
        break;
      case 'navigate':
        if (this.groups.mimesys.includes(Number(this.packageForm.controls.packageTypeId.value))) {
          //settings mimesys
          //generate appuser and appPwd
          let entity: Entity = this.user.childEntities!.find(entity => entity.id == Number(this.packageForm.controls.assignedEntityId.value))!;
          if (entity && entity.entityTypeId === 4) {
            this.settingsService.getMimesys(Number(this.packageForm.controls.assignedEntityId.value), null, entity.parentEntityId, entity.email, this.translateService.currentLang).subscribe(res => {
              /*if (_amount > 1) this.router.navigate(['/licenses/packages'])
              else this.router.navigate(['/licenses/packages/edit/' + (_id ? _id : '')]);*/
            });
          } else {
            /*if (_amount > 1) this.router.navigate(['/licenses/packages'])
            else this.router.navigate(['/licenses/packages/edit/' + (_id ? _id : '')]);*/
          }
        }
        break;
      default: break;
    }
  }

  onChange(id: number, _type?: string) {
    switch (_type) {
      case 'packageType':
        if (id) {
          this.selectedPackageType = this.packageTypes.find(packageType => packageType.id === Number(id))!;
          if (this.selectedPackageType) {
            if (this.action === 'create') {
              this.packageForm.controls.testDays.patchValue((this.selectedPackageType.defaultTestDays ?? this.selectedPackageType.testDays)!.toString());
            } else {
              this.packageForm.controls.testDays.patchValue(this.selectedPackageType.testDays!.toString());
            }
            this.packageForm.controls.private.patchValue(this.selectedPackageType.private!);
          }
          if (this.groups.mimesys.includes(Number(id)) || this.groups.noDevice.includes(Number(id)) || this.groups.fit.includes(Number(id))) {
            this.periods = this.periods.map(period => period.id === 1 ? { ...period, disabled: true } : { ...period, disabled: false });
            this.packageForm.controls.packagePeriodId.patchValue(2);
          } else {
            this.periods = this.periods.map(period => ({ ...period, disabled: false }));
            this.packageForm.controls.packageCategoryId.value == '2'
              ? this.packageForm.controls.packagePeriodId.patchValue(2)
              : this.packageForm.controls.packagePeriodId.patchValue(null);
          }
        }
        break;
      case 'entity':
        if (typeof id === 'number') {
          this.packageForm.controls.packageTypeId.patchValue(null);
          this.packageForm.controls.packagePeriodId.patchValue('');
          this.packageForm.controls.testDays.patchValue('');
          this.filterPackageTypesByPolicy(id);
        }
        this.packageForm.controls.entityId.patchValue(String(id));
        break;
      default:
        break;
    }
  }

  cancel() {
    if (this.action === 'create') {
      this.location.back();
    } else {
      this.reload.emit();
    }
  }

  link() {
    this.licenseService.updatePackage(
      { id: this.package.id, deviceId: this.package.deviceId ? null : Number(this.packageForm.get('deviceId')?.value) } as Package
    ).subscribe(_package => {
      this.package = _package;
      this.package.deviceId = _package.deviceId;
      this.package.device = _package.device;
      this.deviceService.getEntityDevices(this.entity.id).subscribe({
        next: devices => {
          this.filterDevicesByPackageType(devices);
        },
        error: () => this.devices = []
      });
    });
  }

  filterDevicesByPackageType = (_devices: Device[]) => {
    this.devices = _devices.filter(device => ([1, 3]).includes(device.deviceType.id)).map(device => !device.linked && ([1, 3]).includes(device.deviceType.id) ? device : { ...device, disabled: true })
    if (this.package.licenses[0].app.group == 'Mimesys') {
      this.devices = this.devices.filter(device => device.deviceTypeId === 3);
    } else {
      this.devices = this.devices.filter(device => device.deviceTypeId !== 3);
    }
  }
  //delete()
  //delete a package
  delete() {
    this.licenseService.destroyPackage(this.package.id).subscribe(res => {
      this.router.navigate(['/licenses/packages']);
    });
  }

  //deactive()
  //deactive a package
  deactive = () => {
    this.package.deactive = true;
    this.licenseService.updatePackage(this.package).subscribe(res => {
      this.package.deactive = null;
      this.refresh();
    });
  }

  //reactive()
  //reactive a package
  reactive = () => {
    this.package.reactive = true;
    this.licenseService.updatePackage(this.package).subscribe(res => {
      this.package.reactive = null;
      this.refresh();
    });
  }

  //recoverRenewal()
  //recover renewal
  recoverRenewal = () => {
    let packageStatusId: number = this.package.remainingTestDays ? 2 : 3;
    this.licenseService.updatePackage({ id: this.package.id, packageStatusId: packageStatusId, action: 'RECOVERRENEWAL' } as Package).subscribe(res => {
      //this.router.navigate(['/licenses/packages']);
      this.package = res;
    });
  }

  //cancelRenewal()
  //cancel renewal
  cancelRenewal = () => {
    this.licenseService.updatePackage({ id: this.package.id, packageStatusId: 4, action: 'CANCELRENEWAL' } as Package).subscribe(res => {
      //this.router.navigate(['/licenses/packages']);
      this.package = res;
    });
  }

  //cancelUpgrade()
  //cancel upgrade
  cancelUpgrade = () => {
    this.licenseService.cancelUpgrade(this.package.id).subscribe(res => {
      this.router.navigate(['/licenses/packages/edit/' + res.packageOriginId]);
    });
  }

  //resolveEntityTeamId()
  //resolve the team the selected/target entity belongs to (its data is already loaded in childEntities,
  //fallback to the logged user entity). Returns undefined when the entity has no team, in which case
  //policy resolution falls back to the distributor level exactly as before.
  resolveEntityTeamId = (entityId: number): number | undefined => {
    const targetEntity: Entity | undefined =
      this.user.childEntities?.find((child: Entity) => child.id === entityId)
      ?? (this.user.entity?.id === entityId ? this.user.entity : undefined);
    return targetEntity?.teamId ?? undefined;
  }

  //filterPackageTypesByPolicy()
  //filter package types depending on policy from the dist
  filterPackageTypesByPolicy = (entityId: number) => {
    if (entityId != 1) {
      // No-device (360Depict) packages can only be created on optical entities (entityTypeId=4).
      // Because the flow is entity-first, restrict the available package TYPES by the selected entity.
      const selectedEntity = this.user.childEntities!.find((child: Entity) => child.id === entityId);
      const restrictNoDevice = this.action === 'create' && !!selectedEntity && selectedEntity.entityTypeId !== 4;
      // The mirror case: a base entity exists to host no-device packages, so nothing else fits there.
      const onlyNoDevice = this.action === 'create' && !!selectedEntity?.isBase;
      let policyEntityId: number = 0;
      let entityMatch: Entity;
      let parentEntityMatch: Entity;
      if (this.user.entity.entityTypeId === 2 || this.user.entity.entityTypeId === 3) {
        entityMatch = this.user.entity
      } else {
        entityMatch = this.user.childEntities!.find((child: Entity) => child.id === entityId)!;
        switch (entityMatch.entityTypeId) {
          case 4:
            parentEntityMatch = this.user.childEntities!.find((child: Entity) => child.id === entityMatch.parentEntityId)!;
            if (parentEntityMatch.entityTypeId === 3) entityMatch = parentEntityMatch;
            break;
          default:
            break;
        }
      }
      if (entityMatch.entityTypeId >= 3) {
        policyEntityId = entityMatch.parentEntityId
      } else {
        policyEntityId = entityMatch.id
      }
      // team-aware resolution: the selected/target entity's team (if any) drives which policy applies;
      // when it has no team the call sends no teamId and the distributor policy is used as the fallback
      const teamId: number | undefined = this.resolveEntityTeamId(entityId);
      let errorPolicy!: string;
      this.licenseService.showPolicy(policyEntityId, teamId).subscribe({
        next: policy => {
          errorPolicy = policy.error?.error;
          if (errorPolicy) {
            switch (errorPolicy) {
              case 'Policy not found':
                this.packageTypes = this.allPackageTypes.filter(packageType => !packageType.private);
                break;
              default:
                break;
            }
          } else {
            this.policy = policy;
            if (policy.packagePolicies) {
              this.packageTypes = this.allPackageTypes.filter(packageType => policy.packagePolicies.some(packagePolicy => packagePolicy.packageType.id === packageType.id));
              this.packageTypes.map(packageType => packageType.defaultTestDays = policy.packagePolicies.filter(packagePolicy => packagePolicy.packageTypeId === packageType.id)[0]?.defaultTestDays!);
              this.selectedPackageType = this.packageTypes.find(packageType => packageType.id === this.package.packageTypeId)!;
            }
          }
          if (this.action === 'edit') this.narrowToSamePackageGroup();
          if (restrictNoDevice) this.packageTypes = this.packageTypes.filter(packageType => !this.groups.noDevice.includes(packageType.id));
          if (onlyNoDevice) this.packageTypes = this.packageTypes.filter(packageType => this.groups.noDevice.includes(packageType.id));
        },
        error: () => {
          this.packageTypes = this.allPackageTypes.filter(packageType => !packageType.private)
          if (this.action === 'edit') this.narrowToSamePackageGroup();
          if (restrictNoDevice) this.packageTypes = this.packageTypes.filter(packageType => !this.groups.noDevice.includes(packageType.id));
          if (onlyNoDevice) this.packageTypes = this.packageTypes.filter(packageType => this.groups.noDevice.includes(packageType.id));
        }
      });
    }
  }

  //hasOtherTypesInGroup()
  //is there anywhere to modify this package into, inside its own group
  //visible while the catalogue is still loading: hiding it would blink on every package
  hasOtherTypesInGroup = (): boolean => {
    if (!this.allPackageTypes.length) return true;
    const group = this.allPackageTypes.find((packageType: PackageType) => packageType.id === this.package.packageTypeId)?.packageGroup;
    return !!group && this.allPackageTypes.some((packageType: PackageType) => packageType.id !== this.package.packageTypeId && packageType.packageGroup === group);
  }

  //narrowToSamePackageGroup()
  //a package can only be modified into another one of its own group
  //kept outside the policy branch: the fallback to the whole catalogue needs narrowing too
  narrowToSamePackageGroup = () => {
    const currentGroup = this.allPackageTypes.find((packageType: PackageType) => packageType.id === this.package.packageTypeId)?.packageGroup;
    // Unresolved group offers nothing: refusing is the safe side.
    this.packageTypes = currentGroup
      ? this.packageTypes.filter((packageType: PackageType) => packageType.id !== this.package.packageTypeId && packageType.packageGroup === currentGroup)
      : [];
  }

  //actionsPackageLicenseVisibility()
  //set visible package action buttons
  actionsPackageLicenseVisibility = (_action: string) => {
    switch (this.user.roleId) {
      case 1:
      case 2:
      case 3:
        switch (_action) {
          case 'cancel_upgrade':
            switch (this.package.packageCategoryId) {
              case 2:
                /*if (this.package.packageStatusId === 2 && this.package.packageOriginId) return true
                else return false*/
                return false
              default:
                if (this.package.packageStatusId === 6) {
                  return true
                  /*} else if (this.package.packageStatusId === 3 && this.package.packageOriginId) {
                    if (this.activePackageFromUpgradeOnlyInheritedLicenses() || this.activePackageFromUpgradeOnlyNewLicenses()) {
                      return true
                    }*/
                } else {
                  return false
                }
                return false
            }
          case 'modify':
            /*if (this.activePackageFromUpgradeOnlyInheritedLicenses() || this.activePackageFromUpgradeOnlyNewLicenses()) {
              return false
            }*/
            // A group holding a single package type has nowhere to modify into.
            if (!this.hasOtherTypesInGroup()) return false
            switch (this.package.category.name.toLowerCase()) {
              case 'demo':
                return false
              default:
                return this.package.packageStatusId === 2 || this.package.packageStatusId === 3;
            }
          case 'deactivate':
            /*if (this.activePackageFromUpgradeOnlyInheritedLicenses() || this.activePackageFromUpgradeOnlyNewLicenses()) {
              return false
            }*/
            switch (this.user.roleId) {
              case 1:
              case 3:
                switch (this.package.packageStatusId) {//hide deactive button for assigned packages
                  case 1:
                    return false
                  case 2:
                  case 3:
                  case 4://show deactive button for assigned packages
                    switch (this.package.category.name) {
                      case 'demo':
                        return true
                      default:
                        return true
                    }
                  default:
                    return false
                }
              default:
                return false
            }

          case 'reactivate':
            //&& (new Date(this.package.renewalDate) > new Date());
            /*if (this.package.packageStatusId === 5 && !this.package.packageUpgradeId
              && (!this.package.packageOriginId || (this.package.packageOriginId && this.package.activationDate && this.checkRenewalDate()))
            ) {*/
            switch (this.user.roleId) {
              case 1:
              case 3:
                switch (this.package.packageCategoryId) {
                  case 2:
                    if (this.package.packageStatusId === 5 && !this.package.packageUpgradeId) return true
                    else return false
                  default:
                    // "Never got going" needs both dates: a package that needs a device starts its
                    // test when one is linked, but a no-device package (360 Depict standalone) goes
                    // straight to ACTIVE and only ever gets an activationDate. Asking for
                    // testStartDate alone would call it "never started" and let it come back for
                    // good, long after its renewal window closed.
                    if ((this.package.inactiveDate && !this.package.testStartDate && !this.package.activationDate) || (!this.package.packageUpgradeId && this.package.inactiveDate && new Date(this.package.renewalPersDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0))) {
                      return true
                    } else {
                      return false
                    }
                }
              default:
                return false
            }
          /*} else {
            return false
          }*/
          case 'delete_permanently':
            //return false
            if (this.package.packageStatusId === 1) {
              switch (this.user.roleId) {
                case 1:
                case 3:
                  /*switch (this.package.category.name) {
                    case 'demo':
                      return true
                    default:
                      return true
                  }*/
                  return true
                default:
                  return false
              }
            } else {
              return false
            }
          case 'cancel_renewal':
            /*if (this.activePackageFromUpgradeOnlyInheritedLicenses() || this.activePackageFromUpgradeOnlyNewLicenses()) {
              return false
            }*/
            switch (this.package.category.name.toLowerCase()) {
              case 'demo':
                return false
              default:
                switch (this.package.category.name) {
                  case 'demo':
                    return false
                  default:
                    if ((this.package.packageStatusId === 2 || this.package.packageStatusId === 3) && new Date(this.package.renewalPersDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)) return true
                    else return false
                }
            }
          case 'recoverRenewal':
            switch (this.package.category.name) {
              case 'demo':
                return false
              default:
                return this.package.packageStatusId === 4;
            }
          case 'recover':
            switch (this.package.category.name) {
              case 'demo':
                return false
              default:
                return this.package.packageStatusId === 4;
            }
          default:
            switch (this.package.category.name) {
              case 'demo':
                return false
              default:
                return true
            }
        }
      case 4:
        return false
      default:
        return false
    }
  }



  //invoiceable()
  //check if a package is still invoiceable
  invoiceable = () => {
    if (this.package.renewalDate && new Date(this.package.renewalDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)) return true
    else return false
  }

  //inheritedLicensesInvoiceable()
  //check if inherited licenses in a package are still invoiceable
  //this only occurs first time we modify a package
  //use to paint in the view red exclamation in inupgrade status package
  inheritedLicensesInvoiceable = () => {
    const inheritedLicenses = this.package.licenses.filter((license: License) => license.firstPackageId !== license.packageId);
    const newLicenses = this.package.licenses.filter((license: License) => license.firstPackageId === license.packageId);
    if (!inheritedLicenses.length) {
      return false
    } else {
      const firstInheriteLicense = inheritedLicenses[0];
      if (this.package.packageStatusId === 6 && newLicenses.length && this.package.renewalDate && new Date(firstInheriteLicense!.statusDate!.renewalDate).setHours(0, 0, 0, 0) < new Date(this.package.renewalDate).setHours(0, 0, 0, 0)) return true
      else return false
    }
  }

  //onlyInheritedLicenses()
  //detect if a package contains only inherited licenses
  onlyInheritedLicenses = () => {
    const newLicenses = this.package.licenses.filter((license: License) => license.firstPackageId === license.packageId);
    const inheritedLicenses = this.package.licenses.filter((license: License) => license.firstPackageId !== license.packageId);
    if (inheritedLicenses.length && !newLicenses.length) return true
    else return false
  }

  //onlyNewLicenses()
  //detect if a package contains only new licenses
  onlyNewLicenses = () => {
    const newLicenses = this.package.licenses.filter((license: License) => license.firstPackageId === license.packageId);
    const inheritedLicenses = this.package.licenses.filter((license: License) => license.firstPackageId !== license.packageId);
    if (!inheritedLicenses.length && newLicenses.length) return true
    else return false
  }

  //getTestDays()
  //calculata total test days for licenses on the fly 

  toUtcMidnight = (dateStr: string): Date => {
    const date = new Date(dateStr);
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  }

  getTestDays = (_licenseTestDays: number, _licenseStatusDate: StatusDate | undefined) => {
    const millisecondsInDay = 1000 * 60 * 60 * 24;
    const today = new Date();
    const testStartDate = this.toUtcMidnight(_licenseStatusDate!.testStartDate);
    let diffInDays: number = 0;
    let diffInMilliseconds: number = 0;
    if (_licenseStatusDate!.activationDate) {
      const activationDate = this.toUtcMidnight(_licenseStatusDate!.activationDate);
      const diffInMilliseconds = activationDate!.getTime() - testStartDate.getTime();
      diffInDays = diffInMilliseconds / millisecondsInDay;
    } else {
      //it only happens in packages not modified in assigned or test status
      //new modifier package with licenses still in test with its own renewalPers
      diffInMilliseconds = today.getTime() - testStartDate.getTime();
      const diffRenewalPers = (this.toUtcMidnight(_licenseStatusDate!.renewalPersDate).getTime() - today.getTime()) / millisecondsInDay;
      diffInDays = diffInMilliseconds / millisecondsInDay + diffRenewalPers;
    }
    return diffInDays
  }



  getTestDays_bak = (_licenseTestDays: number, _licenseStatusDate: StatusDate | undefined) => {
    const millisecondsInDay = 1000 * 60 * 60 * 24;
    const today = new Date().setHours(0, 0, 0, 0);
    const testStartDate = new Date(_licenseStatusDate!.testStartDate).setHours(0, 0, 0, 0);
    let diffInDays: number = 0;
    let diffInMilliseconds: number = 0;
    if (_licenseStatusDate!.activationDate) {
      const activationDate = new Date(_licenseStatusDate!.activationDate).setHours(0, 0, 0, 0);
      diffInMilliseconds = activationDate - testStartDate;
      diffInDays = Math.ceil(diffInMilliseconds / millisecondsInDay);
    } else {
      //it only happens in packages not modified in assigned or test status
      //new modifier package with licenses still in test with its own renewalPers
      diffInMilliseconds = today - testStartDate;
      const diffRenewalPers = Math.ceil((new Date(_licenseStatusDate!.renewalPersDate).setHours(0, 0, 0, 0) - today) / millisecondsInDay);
      diffInDays = Math.ceil(diffInMilliseconds / millisecondsInDay) + diffRenewalPers;
    }
    return diffInDays
  }

  //activePackageFromUpgradeOnlyInheritedLicenses()
  //detect if an active package born from modify with only inherited licenses to show message
  activePackageFromUpgradeOnlyInheritedLicenses = () => {
    if (this.onlyInheritedLicenses() && new Date(this.package.renewalDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)) {
      return true
    } else {
      return false
    }
  }

  //activePackageFromUpgradeOnlyNewLicenses()
  //detect if an active package born from modify with only new licenses to show message
  activePackageFromUpgradeOnlyNewLicenses = () => {
    if (this.onlyNewLicenses() && new Date(this.package.renewalDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)) {
      return true
    } else {
      return false
    }
  }

  //fullBilled()
  //detect if a package is still billed
  fullBilled = (_package: Package) => {
    //check if package still keep billed
    //_package.licenses.length && 
    if (_package.activationDate && new Date(_package.renewalDate ?? _package.renewalInvoiceableDate ?? _package.renewalPersDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)) return true
    else return false
  }

  //iterateCreation()
  //iterate creation of packages one by one
  //check settings creation at last
  iterateCreation = (_packageForm: any, _amount: number): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      let finalPackageForm: any = { ..._packageForm.value };
      finalPackageForm.amount = 1;
      this.licenseService.insertPackage({ ...finalPackageForm } as unknown as Package).pipe(
        catchError((err) => {
          this.errorCreation = true;
          this.creationError = this.resolveCreationError(err);
          console.error('Error interceptado en el pipe:', err);
          return throwError(() => new Error('Error procesado en el observable'));
        })
      ).pipe(take(1)).subscribe(res => {
        this.packageForm.controls.amount.patchValue(String(Number(_packageForm.controls.amount.value) - 1));
        this.modifyingPackage = false;
        if (Number(_packageForm.controls.amount.value) > 0) {
          this.createdPackagesNumber++;
          this.iterateCreation(_packageForm, _amount);
        } else {
          this.createdPackagesNumber++;
          if (Number(this.packageForm.controls.entityId.value) != 1) {
            if (this.existingPackageId) {
              this.modifyingPackage = true;
              //modify selected package with the new created package
              let finalPackage: Package = res[0];
              const patchedPackage: Package = new Package();
              patchedPackage.id = this.existingPackageId;
              patchedPackage.packageTypeId = finalPackage.packageTypeId;
              patchedPackage.upgrade = finalPackage.id;
              this.licenseService.updatePackage(patchedPackage).subscribe(res => {
                this.endModify = finalPackage.id;
                this.modifyingPackage = false;
                if (this.groups.mimesys.includes(Number(this.packageForm.controls.packageTypeId.value))) {
                  this.setMimesysSettings('navigate', _amount, finalPackage.id);
                } else {
                  if ([5, 6, 8].includes(Number(this.packageForm.controls.packageTypeId.value))) {
                    this.setExternalSettings('navigate', _amount, 5, finalPackage.id);
                  } else if ([7].includes(Number(this.packageForm.controls.packageTypeId.value))) {
                    this.setExternalSettings('navigate', _amount, 7, finalPackage.id);
                  }
                }
                //this.router.navigate(['/licenses/packages/edit/' + finalPackage.id]);
                this.existingPackageId = null;
              });
            } else {
              this.createdPackageId = res[0].id;
              if (this.groups.mimesys.includes(Number(this.packageForm.controls.packageTypeId.value))) {
                this.setExternalSettings('navigate', _amount, 1, res[0].id);
              } else {
                if ([5, 6, 8].includes(Number(this.packageForm.controls.packageTypeId.value))) {
                  this.setExternalSettings('navigate', _amount, 5, res[0].id);
                } else if ([7].includes(Number(this.packageForm.controls.packageTypeId.value))) {
                  this.setExternalSettings('navigate', _amount, 7, res[0].id);
                }
              }
            }
          } else {
            this.createdPackageId = res[0].id;
            //this.router.navigate(['/licenses/packages/edit/' + + (res[0].id ? res[0].id : '')]);
          }
          resolve(true);
        }
      });
    });

  }

  //refresh()
  //refresh content
  refresh = () => {
    this.ngOnInit();
  }

  //convertToNumber()
  //convert string to number
  convertToNumber = (_string: String) => {
    return Number(_string)
  }

  saveComments = () => {
    const patchedPackage: Package = new Package();
    patchedPackage.id = this.package.id;
    patchedPackage.saveComments = true;
    patchedPackage.comments = this.displayComments;
    this.licenseService.updatePackage(patchedPackage).subscribe(res => {
      this.editComments = false;
    });
  }

  // The assign flow's same-group warning is acknowledged by reopening the assign modal, exactly as the
  // package list does: the modal keeps the pending selection and the user confirms from there. Its own
  // dismiss button closes the warning, so nothing else has to be torn down here.
  backConfirmAssignPackageModal(): void {
    utils.forceModal('#reassignModal-assign', false);
  }

  setLicenseData(e: { licenseData: string; noDevice?: boolean }): void {
    this.licenseInactiveData = e.licenseData;
    // The admin wording asks them to confirm a second device, which says nothing for a
    // no-device package: that flow needs its own text.
    this.licenseWarningNoDevice = !!e.noDevice;
  }

  backConfirmCreatePackageModal(e: any) {
    this.underdstoodButton = true;
    utils.forceModal('#confirmationCreatePackageModal', false);
    utils.forceModal('#confirmationModal-admin-some-license', true);
  }

  detectSomeLicenseGroupInactive = () => {
    let packagesToObtain: number[] = [];
    if (this.groups.mimesys.includes(this.packageForm.controls.packageTypeId.value)) {
      packagesToObtain = this.groups.mimesys;
    } else if (this.groups.fit.includes(this.packageForm.controls.packageTypeId.value)) {
      packagesToObtain = this.groups.fit
    }
    //only from the group
    this.createdPackagesIncludesAssigned = this.createdPackagesIncludesAssigned.filter(pack => packagesToObtain.includes(pack.packageTypeId));
    //only by same category
    this.createdPackagesIncludesAssigned = this.createdPackagesIncludesAssigned.filter(pack => pack.packageCategoryId == Number(this.packageForm.controls.packageCategoryId.value));
    //only by same periodicity
    this.createdPackagesIncludesAssigned = this.createdPackagesIncludesAssigned.filter(pack => pack.packagePeriodId == Number(this.packageForm.controls.packagePeriodId.value));
    //only assigned,test,active status
    this.createdPackagesIncludesAssigned = this.createdPackagesIncludesAssigned.filter(pack => ([1, 2, 3]).includes(Number(pack.status.id)));

    this.group = this.allPackageTypes.find((pt: PackageType) => pt.id === this.packageForm.controls.packageTypeId.value)?.appPackages[0].app.group!;
    this.createdPackagesIncludesAssigned?.map((p: Package) => {
      this.allPackageTypes.map((item: PackageType) => item.appPackages[0].app.group === this.group && p.packageTypeId === item.id ? this.haveLicencesSameGroup = true : null)
    })
  }

  //checkRenewalDate()
  //calculate the renewal date on the fly
  //not needed anymore
  /*checkRenewalDate = (): boolean => {
    const today = new Date();
    const daysToAdd = this.package.testDays;
    let pass: boolean = false;
    let renewalDate: Date = new Date(this.package.testStartDate ? this.package.testStartDate : this.package.assignedAt);
    renewalDate.setDate(renewalDate.getDate() + daysToAdd!);
    console.log(renewalDate);
    if (!this.package.remainingTestDays) {
      switch (this.package.packagePeriodId) {
        case 1://monthly
          if (this.calculateNextRenewalDateByMonths(renewalDate, new Date(this.package.inactiveDate)) > new Date()) {
            pass = true
          }
          break;
        case 2://annual
          if (this.calculateNextRenewalDateByYears(renewalDate, new Date(this.package.inactiveDate)) > new Date()) {
            pass = true
          }
          break;
        default:
          break;
      }
    }
    if (renewalDate > today) {
      pass = true
    }
    return pass
  }*/

  //calculateNextRenewalDateByYears()
  //given an initial renewal calculate through the years the next renewal from another reference date
  //not neede anymore
  /*calculateNextRenewalDateByYears = (renewalDateIni: Date, refDate: Date) => {
    // Convert the renewalDateIni date to a Date object
    const startDate = renewalDateIni;

    // Clone the start date to avoid mutating the original
    let nextInferiorDate = new Date(startDate);

    // Increment the year until the next date exceeds refDate
    while (nextInferiorDate <= refDate) {
      nextInferiorDate.setFullYear(nextInferiorDate.getFullYear() + 1);
    }

    return nextInferiorDate;
  }*/

  //calculateNextRenewalDateByMonths()
  //given an initial renewal calculate through the months the next renewal from another reference date
  //not needed anymore
  /*calculateNextRenewalDateByMonths = (renewalDateIni: Date, refDate: Date) => {
    // Convert the renewalDateIni date to a Date object
    const startDate = new Date(renewalDateIni);

    // Clone the start date to avoid mutating the original
    let nextInferiorDate = new Date(startDate);

    // Increment the month until the next date exceeds refDate
    while (nextInferiorDate <= refDate) {
      nextInferiorDate.setMonth(nextInferiorDate.getMonth() + 1);
    }

    return nextInferiorDate;
  }*/


  //recalculateRenewal()
  //recalculate renewal on the fly
  //not needed anymore
  /*recalculateRenewal = (_package: Package) => {
    const daysToAdd = _package.testDays;
    let renewalDate: Date = new Date(this.package.testStartDate ? this.package.testStartDate : this.package.assignedAt);
    renewalDate.setDate(renewalDate.getDate() + daysToAdd!);
    switch (_package.packagePeriodId) {
      case 1:
        return new Date(this.calculateNextRenewalDateByMonths(renewalDate, new Date(new Date().setHours(0, 0, 0, 0)))).toDateString()
      case 2:
        return new Date(this.calculateNextRenewalDateByYears(renewalDate, new Date(new Date().setHours(0, 0, 0, 0)))).toDateString()
    }
    return new Date().toDateString()
  }*/

  //getTestDaysInheritedLicense()
  //calculate total tesdays for any license through its data
  //not needed anymore, now getTestDays is used
  /*getTestDaysInheritedLicense = (_packageTestStartDate: string, _testStartDate: string, _licenseStatusDate: StatusDate | undefined, _testDays: number) => {
    const millisecondsInDay = 1000 * 60 * 60 * 24;

    if (_licenseStatusDate!.activationDate && _licenseStatusDate!.testStartDate) {
      const activationDate = new Date(_licenseStatusDate!.activationDate).setHours(0, 0, 0, 0);
      const testStartDate = new Date(_licenseStatusDate!.testStartDate).setHours(0, 0, 0, 0);
      const diffInMillisecondsActive: number = activationDate - testStartDate;
      const diffInDaysActive = Math.ceil(diffInMillisecondsActive / millisecondsInDay);
      return diffInDaysActive
    }

    const date1 = new Date(_testStartDate).setHours(0, 0, 0, 0);
    const date2 = new Date().setHours(0, 0, 0, 0);
    const diffInMilliseconds: number = date2 - date1;
    const diffInDays = Math.floor(diffInMilliseconds / millisecondsInDay);

    const date3 = new Date(_packageTestStartDate).setHours(0, 0, 0, 0);
    const diffInMillisecondsPackage: number = date2 - date3;
    const diffInDaysPackage = Math.floor(diffInMillisecondsPackage / millisecondsInDay);

    return diffInDays + _testDays //diffInDays + (_testDays - diffInDaysPackage)
  }*/

  //leaveOutPackageFromUpgradeOnlyInheritedLicenses()
  //leave out this package from modifiable package list if only has inherited licenses
  //not needed anymore because now modification flow taking in account billed licenses before
  /*leaveOutPackageFromUpgradeOnlyInheritedLicenses = (_package: Package) => {
    if (this.onlyInheritedLicensesLeaveOut(_package.licenses) && new Date(_package.renewalInvoiceableDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)) {
      return true
    } else {
      return false
    }
  }
  onlyInheritedLicensesLeaveOut = (_licenses: License[]) => {
    const newLicenses = _licenses.filter((license: License) => license.firstPackageId === license.packageId);
    const inheritedLicenses = _licenses.filter((license: License) => license.firstPackageId !== license.packageId);
    if (inheritedLicenses.length && !newLicenses.length) return true
    else return false
  }
  */

  //leaveOutPackageFromUpgradeOnlyNewLicenses()
  //leave out this package from modifiable package list if only has new licenses
  //not needed anymore
  /*leaveOutPackageFromUpgradeOnlyNewLicenses = (_package: Package) => {
    if (this.onlyNewLicensesLeaveOut(_package.licenses) && new Date(_package.renewalInvoiceableDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)) {
      return true
    } else {
      return false
    }
  }
  onlyNewLicensesLeaveOut = (_licenses: License[]) => {
    const newLicenses = _licenses.filter((license: License) => license.firstPackageId === license.packageId);
    const inheritedLicenses = _licenses.filter((license: License) => license.firstPackageId !== license.packageId);
    if (!inheritedLicenses.length && newLicenses.length) return true
    else return false
  }  
  */

  //recoverablePackageFromUpgradeOnlyInheritedLicenses()
  //detect if a package has only inherited licenses and those are still billed
  //not needed anymore
  /*recoverablePackageFromUpgradeOnlyInheritedLicenses = (_package: Package, _licenses: License[]) => {
    if (this.onlyInheritedLicensesUpgrade(_licenses) && new Date(_package.renewalPersDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)) {
      return true
    } else {
      return false
    }
  }
  onlyInheritedLicensesUpgrade = (_licenses: License[]) => {
    const newLicenses = _licenses.filter((license: License) => license.firstPackageId === license.packageId);
    const inheritedLicenses = _licenses.filter((license: License) => license.firstPackageId !== license.packageId);
    if (inheritedLicenses.length && !newLicenses.length) return true
    else return false
  }  
  */

  //recoverablePackageFromUpgradeOnlyNewLicenses()
  //not needed anymore
  /*recoverablePackageFromUpgradeOnlyNewLicenses = (_package: Package, _licenses: License[]) => {
    if (this.onlyNewLicensesUpgrade(_licenses) && new Date(_package.renewalPersDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)) {
      return true
    } else {
      return false
    }
  }
  onlyNewLicensesUpgrade = (_licenses: License[]) => {
    const newLicenses = _licenses.filter((license: License) => license.firstPackageId === license.packageId);
    const inheritedLicenses = _licenses.filter((license: License) => license.firstPackageId !== license.packageId);
    if (!inheritedLicenses.length && !newLicenses.length) return true
    else return false
  }  
  */

  //preventFromTimezone()
  //prevent from wrong time zon definition
  //not needed anymore
  /*preventFromTimezone = (_date: string) => {
    if (_date) {
      console.log(_date);
      console.log(new Date(_date.split('T')[0]).toLocaleDateString());
      //const tzoffset = new Date(_date).getTimezoneOffset() * 60000; //offset in milliseconds
      //const withoutTimezone = new Date(new Date(_date).valueOf() - tzoffset)
      // .toISOString()
      // .slice(0, -1);
      //return withoutTimezone;
      var dateString = new Date(_date.split('T')[0]).toLocaleDateString(); // Oct 23
      var dateParts = dateString.split("/");
      // month is 0-based, that's why we need dataParts[1] - 1
      var dateObject = new Date(+dateParts[2], Number(dateParts[1]) - 1, +dateParts[0]);
      return `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`
    } else {
      return true
    }
  };*/

}
